import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  HStack,
  Stack,
  Button,
  IconButton,
  useToast 
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { getVideoData, updateVideoData } from '../../../services/Videos';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import ImageWidget from '../../../CommonComponents/ImageWidget';
import ImagePreview from '../../../components/ImagePreview';
import FormLoader from '../../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';

const Section3 = () => {

  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();

  const transFormData = (data) => {
    const insideImageArray = Array.isArray(data?.inside_image)
      ? data.inside_image
      : (data?.inside_image ? data.inside_image.split('|').map(s => s.trim()).filter(Boolean) : []);

    return {
      ...data,
      preview_cover_image: data?.cover_image,
      preview_back_cover_image: data?.back_cover_image,
      inside_image: insideImageArray,
      preview_inside_image: insideImageArray[0] || ''
    };
  };

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'title', 'sub_title', 'year', 'sortable_year', 'actual_date', 'date_of_artwork', 'medium_artwork', 'cast', 'director', 'producer', 'banner', 'cover_image', 'preview_cover_image', 'back_cover_image', 'preview_back_cover_image', 'inside_image', 'preview_inside_image', 'cine_object_title','cine_object_description', 'cine_object_actual_date'];
    const rightFields = ['auction_house_name', 'country_of_sale', 'auction_title_and_sub_title', 'auction_year_of_event', 'auction_start_date', 'auction_end_date', 'sortable_auction_end_date', 'venue', 'city_of_event', 'signature', 'source_alias', 'external_source', 'external_notes', 'source_alias2', 'source_alias3', 'cdt_release_type', 'featured_artist'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "title": {
        "type": "string",
        "title": "Title",
      },
      "sub_title": {
        "type": "string",
        "title": "Sub Title"
      },
      "year": {
        "type": "string",
        "title": "Year"
      },
      "sortable_year": {
        "type": "string",
        "title": "Sortable year"
      },
      "actual_date": {
        "type": "string",
        "title": "Actual date"
      },
      "date_of_artwork": {
        "type": "string",
        "title": "Date of artwork"
      },
      "medium_artwork": {
        "type": "string",
        "title": "Medium artwork"
      },
      "cast": {
        "type": "string",
        "title": "Cast"
      },
      "director": {
        "type": "string",
        "title": "Director"
      },
      "producer": {
        "type": "string",
        "title": "Producer"
      },
      "banner": {
        "type": "string",
        "title": "Banner"
      },
      "cover_image": {
        "type": "string",
        "title": "Cover Image"
      },
      "preview_cover_image": {
        "type": "string",
        "title": "Image URL"
      },
      "inside_image": {
        "type": "array",
        "title": "Inside Image",
        "items": { "type": "string" }
      },
      "preview_inside_image": {
        "type": "string",
        "title": "Image URL"
      },
      "cine_object_title": {
        "type": "string",
        "title": "Cine Object Title"
      },
      "cine_object_description": {
        "type": "string",
        "title": "Cine Object Description"
      },
      "cine_object_actual_date": {
        "type": "string",
        "title": "Cine Object Actual Date"
      }
    }
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "auction_house_name": {
        "type": "string",
        "title": "Auction House Name"
      },
      "country_of_sale": {
        "type": "string",
        "title": "Country of Sale"
      },
      "auction_title_and_sub_title": {
        "type": "string",
        "title": "Auction Title and Sub Title"
      },
      "auction_year_of_event": {
        "type": "string",
        "title": "Auction Year of Event"
      },
      "auction_start_date": {
        "type": "string",
        "title": "Auction Start Date"
      },
      "auction_end_date": {
        "type": "string",
        "title": "Auction End Date"
      },
      "sortable_auction_end_date": {
        "type": "string",
        "title": "Sortable auction end date",
        "format": "date-time",
        "default": "",
      },
      "venue": {
        "type": "string",
        "title": "Venue"
      },
      "city_of_event": {
        "type": "string",
        "title": "City of Event"
      },
      "signature": {
        "type": "string",
        "title": "Signature"
      },
      "source_alias": {
        "type": "string",
        "title": "Source Alias"
      },
      "external_source": {
        "type": "string",
        "title": "External Source"
      },
      "external_notes": {
        "type": "string",
        "title": "External Notes"
      },
      "source_alias2": {
        "type": "string",
        "title": "Source Alias 2"
      },
      "source_alias3": {
        "type": "string",
        "title": "Source Alias 3"
      },
      "cdt_release_type": {
        "type": "string",
        "title": "CDT Release Type"
      },
      "featured_artist": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Featured Artist"
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
    "title": "Quadrant-3 fields",
    "description": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "title": {
        "type": "string",
        "title": "Title",
      },
      "sub_title": {
        "type": "string",
        "title": "Sub Title"
      },
      "year": {
        "type": "string",
        "title": "Year"
      },
      "sortable_year": {
        "type": "string",
        "title": "Sortable year"
      },
      "actual_date": {
        "type": "string",
        "title": "Actual date"
      },
      "date_of_artwork": {
        "type": "string",
        "title": "Date of artwork"
      },
      "medium_artwork": {
        "type": "string",
        "title": "Medium artwork"
      },
      "cast": {
        "type": "string",
        "title": "Cast"
      },
      "director": {
        "type": "string",
        "title": "Director"
      },
      "producer": {
        "type": "string",
        "title": "Producer"
      },
      "banner": {
        "type": "string",
        "title": "Banner"
      },
      "cover_image": {
        "type": "string",
        "title": "Cover Image"
      },
      "preview_cover_image": {
        "type": "string",
        "title": "Image URL"
      },
      "inside_image": {
        "type": "array",
        "title": "Inside Image",
        "items": { "type": "string" }
      },
      "preview_inside_image": {
        "type": "string",
        "title": "Image URL"
      },
      "auction_house_name": {
        "type": "string",
        "title": "Auction House Name"
      },
      "country_of_sale": {
        "type": "string",
        "title": "Country of Sale"
      },
      "auction_title_and_sub_title": {
        "type": "string",
        "title": "Auction Title and Sub Title"
      },
      "auction_year_of_event": {
        "type": "string",
        "title": "Auction Year of Event"
      },
      "auction_start_date": {
        "type": "string",
        "title": "Auction Start Date"
      },
      "auction_end_date": {
        "type": "string",
        "title": "Auction End Date"
      },
      "sortable_auction_end_date": {
        "type": "string",
        "title": "Sortable auction end date",
        "format": "date-time",
        "default": "",
      },
      "venue": {
        "type": "string",
        "title": "Venue"
      },
      "city_of_event": {
        "type": "string",
        "title": "City of Event"
      },
      "signature": {
        "type": "string",
        "title": "Signature"
      },
      "source_alias": {
        "type": "string",
        "title": "Source Alias"
      },
      "featured_artist": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Featured Artist"
      }
    }
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    title: { "ui:emptyValue": "" },
    sub_title: { "ui:emptyValue": "" },
    year: { "ui:emptyValue": "" },
    sortable_year: { "ui:emptyValue": "" },
    actual_date: { "ui:emptyValue": "" },
    date_of_artwork: { "ui:emptyValue": "" },
    medium_artwork: { "ui:emptyValue": "" },
    cast: { "ui:emptyValue": "" },
    director: { "ui:emptyValue": "" },
    producer: { "ui:emptyValue": "" },
    banner: { "ui:emptyValue": "" },
    preview_cover_image: {
      "ui:widget": ImageWidget,
      "ui:emptyValue": "",
    },
    preview_back_cover_image: {
      "ui:widget": ImageWidget,
      "ui:emptyValue": "",
    },
    preview_inside_image: {
      "ui:widget": ImageWidget,
      "ui:emptyValue": "",
    },
    cover_image: { "ui:emptyValue": "" },
    back_cover_image: { "ui:emptyValue": "" },
    inside_image: { "ui:emptyValue": [] },
    cine_object_title: { "ui:emptyValue": "" },
    cine_object_description: { "ui:emptyValue": "" },
    cine_object_actual_date: { "ui:emptyValue": "" }
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
    },
    auction_start_date: { "ui:emptyValue": "" },
    auction_end_date: { "ui:emptyValue": "" },
    sortable_auction_end_date: { "ui:emptyValue": "" },
    auction_house_name: { "ui:emptyValue": "" },
    country_of_sale: { "ui:emptyValue": "" },
    auction_title_and_sub_title: { "ui:emptyValue": "" },
    auction_year_of_event: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    city_of_event: { "ui:emptyValue": "" },
    signature: { "ui:emptyValue": "" },
    source_alias: { "ui:emptyValue": "" },
    source_alias2: { "ui:emptyValue": "" },
    source_alias3: { "ui:emptyValue": "" },
    cdt_release_type: { "ui:emptyValue": "" },
    featured_artist: {
      "ui:emptyValue": "",
      "ui:readonly": true,
      "items": {
        "ui:emptyValue": "",
      },
    }
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    preview_cover_image: {
      "ui:widget": ImageWidget,
      "ui:emptyValue": "",
    },
    preview_back_cover_image: {
      "ui:widget": ImageWidget,
      "ui:emptyValue": "",
    },
    preview_inside_image: {
      "ui:widget": ImageWidget,
      "ui:emptyValue": "",
    },
    auction_start_date: { "ui:emptyValue": "" },
    auction_end_date: { "ui:emptyValue": "" },
    sortable_auction_end_date: { "ui:emptyValue": "" },
    sortable_year: { "ui:emptyValue": "" },
    actual_date: { "ui:emptyValue": "" },
    date_of_artwork: { "ui:emptyValue": "" },
    medium_artwork: { "ui:emptyValue": "" },
    cast: { "ui:emptyValue": "" },
    director: { "ui:emptyValue": "" },
    producer: { "ui:emptyValue": "" },
    banner: { "ui:emptyValue": "" },
    cover_image: { "ui:emptyValue": "" },
    back_cover_image: { "ui:emptyValue": "" },
    inside_image: { "ui:emptyValue": [] },
    auction_house_name: { "ui:emptyValue": "" },
    country_of_sale: { "ui:emptyValue": "" },
    auction_title_and_sub_title: { "ui:emptyValue": "" },
    auction_year_of_event: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    city_of_event: { "ui:emptyValue": "" },
    signature: { "ui:emptyValue": "" },
    source_alias: { "ui:emptyValue": "" },
    featured_artist: {
      "ui:emptyValue": "",
      "ui:readonly": true,
      "items": {
        "ui:emptyValue": "",
      },
    },
    title: { "ui:emptyValue": "" },
    sub_title: { "ui:emptyValue": "" },
    year: { "ui:emptyValue": "" }
  };

  // Helper function to get first available image
  const getFirstAvailableImage = (formData) => {
    return formData?.cover_image || 
           formData?.preview_cover_image || 
           formData?.back_cover_image || 
           formData?.preview_back_cover_image || 
           formData?.inside_image || 
           formData?.preview_inside_image || 
           null;
  };
  
  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getVideoData(alias, 'section-3');
          if (result) {
            const transformedData = transFormData(result?.data?.[0]);
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }
      
      // Convert inside_image array back to pipe-separated string for the backend
      if (Array.isArray(finalFormData.inside_image)) {
        finalFormData = {
          ...finalFormData,
          inside_image: finalFormData.inside_image.filter(Boolean).join(' | ')
        };
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateVideoData(alias, finalFormData);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">Videos Quadrant-3 Fields</Box>
                  <IconButton
                    aria-label={showImagePreview ? "Hide image preview" : "Show image preview"}
                    icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    colorScheme={showImagePreview ? "red" : "blue"}
                    onClick={() => setShowImagePreview(prev => !prev)}
                    title={showImagePreview ? "Close image preview" : "Open image preview"}
                  />
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview && (
                <HStack sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}>
                  <ImagePreview image={getFirstAvailableImage(combinedFormData)} />
                </HStack>
              )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">Videos Quadrant-3 Fields</Box>
                      <IconButton
                        aria-label={showImagePreview ? "Hide image preview" : "Show image preview"}
                        icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        colorScheme={showImagePreview ? "red" : "blue"}
                        onClick={() => setShowImagePreview(prev => !prev)}
                        title={showImagePreview ? "Close image preview" : "Open image preview"}
                      />
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview && (
                <HStack sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}>
                  <ImagePreview image={getFirstAvailableImage(combineFormData(leftFormData, rightFormData))} />
                </HStack>
              )}
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section3;